/*
 * premierstreets
 * version 4.5
 * http://www.premierstreets.com
 *
 * Copyright (c) 2021 premier streets
 * Author premier streets <support@premierstreets.com>
 * Licensed under the MIT license.
 */
import { BrowserModule, BrowserTransferStateModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgxSpinnerModule } from 'ngx-spinner';
import { OverlayContainer, Overlay } from '@angular/cdk/overlay';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { CustomOverlayContainer } from './theme/utils/custom-overlay-container';
import { menuScrollStrategy } from './theme/utils/scroll-strategy';
import { AppSettings } from './app.settings';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfigService } from '../core/service/config.service';
import { RequestInterceptor } from '../core/service/request.interceptor';
import { AuthGuard } from '../core/service/auth.guard';
import { JwSocialButtonsModule } from 'jw-angular-social-buttons';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';

// modules
import { ComponentsModule } from './shared/components/index';
import { DefaultRoutingModule } from './default.routing';
import { SharedModule } from './shared/shared.module';

// components
import { CONTAINERS } from './common/index';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { DefaultComponent } from './default.component';

// store actions
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { metaReducers, reducers } from '../core/reducer.interface';
import { ListsEffect } from '../core/lists/effects/lists.effect';
import { ListsSandbox } from '../core/lists/lists.sandbox';
import { ListsService } from '../core/lists/lists.service';
import { PostService } from '../core/posts/post.service';
// component
import { DetailPageComponent } from './pages/detail-page/detail-page.component';
import { LayoutAuthGuard } from '../core/service/layout_auth.guard';
import { UnderDevelopingComponent } from './pages/layout/under-developing/under-developing.component';
import { ShareButtonModule } from '@ngx-share/button';
import { AgmCoreModule } from '@agm/core';
import { SocialLoginModule } from './shared/social-login/auth.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { APP_BASE_HREF, DOCUMENT, isPlatformBrowser, ɵgetDOM } from '@angular/common';
import { WishlistSandbox } from '../core/wishlist/wishlist.sandbox';
import { WishlistService } from '../core/wishlist/wishlist.service';
import { WishlistEffect } from '../core/wishlist/effects/wishlist.effect';
import { CartEffect } from '../core/cart/effects/cart.effect';
import { CartSandbox } from '../core/cart/cart.sandbox';
import { CartService } from '../core/cart/cart.service';
import { AccountService } from '../core/account/account.service';
import { NgDialogAnimationService } from 'ng-dialog-animation';
import { AuthSandbox } from '../core/auth/auth.sandbox';
import { SetPasswordComponent } from './pages/Authentication/set-password/set-password.component';
import { AuthEffects } from '../core/auth/effects/auth.effect';
import { AuthApiService } from '../core/auth/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BlogsSandbox } from '../core/blogs/blogs.sandbox';
import { MarketPlaceComponent } from './pages/marketplace/marketplace.component';
//import { EntertainmentOutsideVenueComponent } from './pages/entertainment-outside-venue/entertainment-outside-venue.component';

import { NgxPayPalModule } from 'ngx-paypal';
import { EntertainmentOutsideVenueComponent } from './pages/entertainment-outside-venue/entertainment-outside-venue.component';
import { LinkCanonicalService } from '../core/canonical/canonical.service';
import { SnowFlakeComponent } from './pages/marketplace/snow-fall/snow-fall.component';
import { RainFallComponent } from './pages/marketplace/rain-fall/rain-fall.component';
import { TemplateService } from '../core/template/template.service';
import { GoogleAnalyticsService } from '../core/googla-analytics/google-analytics.service';

//import { GoogleTagManagerModule } from 'angular-google-tag-manager';

import { CommonSandbox } from '../core/common/common.sandbox';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BookingModalComponent } from './shared/components/booking-modal/booking-modal.component';


export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

@NgModule({
    imports: [
        BrowserModule.withServerTransition({
            appId: 'premierstreets'
        }),
        BrowserTransferStateModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        NgxSpinnerModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        ComponentsModule,
        JwSocialButtonsModule,
        NgxGoogleAnalyticsModule.forRoot('G-ND6DVVKYM8'),
        NgxGoogleAnalyticsRouterModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgxPayPalModule,
        EffectsModule.forRoot([ListsEffect, WishlistEffect, CartEffect, AuthEffects]),
        StoreModule.forRoot(reducers, { metaReducers }),
        ShareButtonModule.withConfig({
            debug: true
        }),
        AgmCoreModule.forRoot({
            apiKey: 'AIzaSyCfuTV5tYInC-Mj6YJ9LzRocVOdn3yCUGM'
        }),
        // GoogleTagManagerModule.forRoot({
        //     id: "AW-527611733"
        //   }),
        NgbModule,
        DefaultRoutingModule,
        SocialLoginModule,
        ServiceWorkerModule.register('/ngsw-worker.js', { enabled: environment.production }),

    ],
    declarations: [
        DefaultComponent,
        NotFoundComponent,
        DetailPageComponent,
        MarketPlaceComponent,
        EntertainmentOutsideVenueComponent,
        CONTAINERS.LayoutContainerComponent,
        UnderDevelopingComponent,
        SetPasswordComponent,
        SnowFlakeComponent,
        RainFallComponent,
        BookingModalComponent
        //  ChatDialog
    ],
    providers: [
        AuthSandbox,
        AppSettings,
        LayoutAuthGuard,
        AuthGuard,
        NgDialogAnimationService,
        ConfigService,
        { provide: OverlayContainer, useClass: CustomOverlayContainer },
        { provide: MAT_MENU_SCROLL_STRATEGY, useFactory: menuScrollStrategy, deps: [Overlay] },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestInterceptor,
            multi: true
        },
        { provide: APP_BASE_HREF, useValue: '' },
        {
            provide: APP_INITIALIZER,
            useFactory: function (document: Document, platformId: Object): Function {
                return () => {
                    if (isPlatformBrowser(platformId)) {
                        const dom = ɵgetDOM();
                        const styles: any[] = Array.prototype.slice.apply(dom.getDefaultDocument().querySelectorAll(`style[ng-transition]`));
                        styles.forEach(el => {
                            // Remove ng-transition attribute to prevent Angular appInitializerFactory
                            // to remove server styles before preboot complete
                            el.removeAttribute('ng-transition');
                        });
                        document.addEventListener('PrebootComplete', () => {
                            // After preboot complete, remove the server scripts
                            setTimeout(() => styles.forEach(el => dom.remove(el)));
                        });
                    }
                };
            },
            deps: [DOCUMENT, PLATFORM_ID],
            multi: true
        },
        ListsSandbox,
        BlogsSandbox,
        ListsService,
        WishlistSandbox,
        WishlistService, CartSandbox, CartService,
        AccountService,
        AuthApiService,
        PostService,
        LinkCanonicalService,
        TemplateService,
        GoogleAnalyticsService,
        CommonSandbox


    ],
    entryComponents: [BookingModalComponent],
    bootstrap: [DefaultComponent]
})
export class DefaultModule {
}
